import { Dialog, Transition } from '@headlessui/react';
import { Fragment, type HTMLAttributes, useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { SettlementApi } from '../../api';
import { useTerminalStore } from '../../store';

type Props = {
  isOpen: boolean;
  onClose: () => void;
} & HTMLAttributes<HTMLDivElement>;

const formatNumberInput = (value: string): string => {
  const cleaned = value.replace(/[^\d.,]/g, '');
  if (!cleaned) return '';

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');

  let normalized = cleaned;
  if (hasComma && hasDot) {
    const commaIndex = cleaned.indexOf(',');
    const dotIndex = cleaned.indexOf('.');
    if (commaIndex < dotIndex) {
      normalized = cleaned.replace(/,/g, '');
    } else {
      normalized = cleaned.replace(/\./g, '');
      normalized = normalized.replace(',', '.');
    }
  } else if (hasComma) {
    normalized = cleaned.replace(',', '.');
  }

  const parts = normalized.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  if (parts.length === 2 && parts[1].length > 2) {
    return parts[0] + '.' + parts[1].slice(0, 2);
  }
  return normalized;
};

const parseNumber = (value: string): number => {
  const normalized = value.replace(',', '.');
  return parseFloat(normalized) || 0;
};

export const ManualSettlementDialog = ({ isOpen, onClose }: Props) => {
  const { terminalId } = useTerminalStore();
  const [amountInput, setAmountInput] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const amount = useMemo(() => (amountInput ? parseNumber(amountInput) : 0), [amountInput]);
  const canSubmit = Boolean(terminalId) && amount > 0;

  const { mutate, isPending } = useMutation({
    mutationFn: SettlementApi.createManualSettlement,
    onSuccess: () => {
      toast.success('Success !');
      setAmountInput('');
      setFieldError(null);
      onClose();
    },
    onError: (error: any) => {
      if (SettlementApi.isCheckError(error)) {
        const fieldDescription = error.response?.data?.field?.description;
        setFieldError(fieldDescription || 'Invalid amount');
        return;
      }
      toast.error('Manual settlement failed');
    },
  });

  const handleOk = useCallback(() => {
    if (!terminalId) return;
    if (!amount || amount <= 0) {
      setFieldError('Field is required');
      return;
    }
    mutate({ terminalId, amount: parseFloat(amount.toFixed(2)) });
  }, [amount, mutate, terminalId]);

  const handleCancel = useCallback(() => {
    setAmountInput('');
    setFieldError(null);
    onClose();
  }, [onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[120]" onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-white px-8 py-8 rounded-3xl w-[520px]">
                <div className="grid gap-5 text-left">
                  <h3 className="text-lg font-bold text-brand-6">Manual settlement</h3>

                  <div>
                    <label className="block text-sm text-brand-2 mb-2">Amount in local currency</label>
                    <input
                      className={`input w-full ${fieldError ? 'border-red-500 border-2' : ''}`}
                      value={amountInput}
                      onChange={(e) => {
                        setAmountInput(formatNumberInput(e.target.value));
                        setFieldError(null);
                      }}
                      type="text"
                      inputMode="decimal"
                      placeholder="Enter amount"
                      disabled={isPending}
                    />
                    {fieldError && <span className="block mt-1 text-sm text-red-600">{fieldError}</span>}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <button
                      type="button"
                      className="button button--block button--primary h-12 flex items-center justify-center text-center"
                      disabled={!canSubmit || isPending}
                      onClick={handleOk}
                    >
                      OK
                    </button>
                    <button
                      type="button"
                      className="button button--block bg-orange-500 text-white h-12 flex items-center justify-center text-center"
                      disabled={isPending}
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

